import React, { useState, useMemo, useRef, useEffect } from 'react';

const UserList = ({ users, onEdit, onDelete, onImpersonate }) => {
    const [activeTab, setActiveTab] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const tableScrollRef = useRef(null);
    const [scrollAreaHeight, setScrollAreaHeight] = useState(null);

    // Dynamically calculate available height for the table scroll area
    // This mirrors the Orders table approach: measure the scroll container's
    // top offset from the viewport and set an explicit pixel height so the
    // browser knows to scroll internally rather than growing the page.
    useEffect(() => {
        const updateScrollHeight = () => {
            if (!tableScrollRef.current) return;
            const rect = tableScrollRef.current.getBoundingClientRect();
            // 56px accounts for the footer bar + some bottom padding
            const available = Math.max(200, window.innerHeight - rect.top - 56);
            setScrollAreaHeight(available);
        };

        // Small delay to let layout settle after mount
        const timerId = setTimeout(updateScrollHeight, 50);
        window.addEventListener('resize', updateScrollHeight);
        return () => {
            clearTimeout(timerId);
            window.removeEventListener('resize', updateScrollHeight);
        };
    }, [users.length, activeTab]);

    // Tabs configuration
    const tabs = [
        { id: 'all', label: 'All Users' },
        { id: 'admin', label: 'Admins' },
        { id: 'client', label: 'Clients' },
        { id: 'vendor', label: 'Vendors' }
    ];

    // Filter and Search Logic
    const filteredUsers = useMemo(() => {
        return users.filter(user => {
            // Role Filter
            if (activeTab !== 'all' && user.role !== activeTab) return false;

            // Search Filter
            if (searchQuery) {
                const query = searchQuery.toLowerCase();
                return (
                    user.name?.toLowerCase().includes(query) ||
                    user.email?.toLowerCase().includes(query) ||
                    user.phone?.includes(query) ||
                    user.company?.toLowerCase().includes(query)
                );
            }
            return true;
        });
    }, [users, activeTab, searchQuery]);

    // Export to CSV
    const handleExport = () => {
        // Define headers
        const headers = ['Name', 'Email', 'Role', 'Phone', 'Company', 'Address', 'Created At'];

        // Map user data to rows
        const rows = filteredUsers.map(user => [
            user.name,
            user.email,
            user.role,
            user.phone || '',
            user.company || '',
            user.address || '',
            user.created_at || ''
        ]);

        // Construct CSV content
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell || ''}"`).join(','))
        ].join('\n');

        // Create download link
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', 'users_export.csv');
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const getRoleBadge = (role) => {
        const styles = {
            admin: 'bg-red-100 text-red-800 border-red-200',
            client: 'bg-green-100 text-green-800 border-green-200',
            vendor: 'bg-blue-100 text-blue-800 border-blue-200'
        };
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[role] || 'bg-gray-100 text-gray-800'}`}>
                {role.charAt(0).toUpperCase() + role.slice(1)}
            </span>
        );
    };

    const getInitials = (name) => {
        if (!name) return '??';
        return name
            .split(' ')
            .map(n => n[0])
            .slice(0, 2)
            .join('')
            .toUpperCase();
    };

    const getAvatarColor = (role) => {
        const colors = {
            admin: 'bg-red-500',
            client: 'bg-green-500',
            vendor: 'bg-blue-500'
        };
        return colors[role] || 'bg-gray-500';
    };

    return (
        <div className="flex flex-col h-full bg-white rounded-lg shadow-lg border border-gray-200">
            {/* Header & Controls */}
            <div className="flex-shrink-0 p-4 border-b border-gray-200 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">

                    {/* Tabs */}
                    <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg self-start">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`px-4 py-1 text-sm font-medium rounded-md transition-all ${activeTab === tab.id
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Search & Export */}
                    <div className="flex items-center space-x-2">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Search users..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 w-64"
                            />
                            <svg className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>

                        <button
                            onClick={handleExport}
                            className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                            title="Export to CSV"
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Export
                        </button>
                    </div>
                </div>
            </div>

            {/* Table - scrollable area with calculated height */}
            <div
                ref={tableScrollRef}
                className="overflow-auto relative"
                style={scrollAreaHeight ? { height: scrollAreaHeight } : { flex: '1 1 auto', minHeight: 0 }}
            >
                <table className="w-full relative border-collapse">
                    <thead className="sticky top-0 bg-gray-50 z-10 shadow-sm">
                        <tr className="border-b border-gray-200">
                            <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">User</th>
                            <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">Role</th>
                            <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">Contact Info</th>
                            <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">Company/Address</th>
                            <th className="text-right py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {filteredUsers.length > 0 ? (
                            filteredUsers.map(user => (
                                <tr key={user.id} className="hover:bg-gray-50 transition-colors group">
                                    <td className="py-2 px-6 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className={`flex-shrink-0 h-8 w-8 rounded-full ${getAvatarColor(user.role)} flex items-center justify-center text-white font-bold text-xs`}>
                                                {getInitials(user.name)}
                                            </div>
                                            <div className="ml-3">
                                                <div className="text-sm font-medium text-gray-900">{user.name}</div>
                                                <div className="text-xs text-gray-500">Created: {new Date(user.created_at).toLocaleDateString()}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="py-2 px-6 whitespace-nowrap">
                                        {getRoleBadge(user.role)}
                                    </td>
                                    <td className="py-2 px-6 whitespace-nowrap">
                                        <div className="flex flex-col">
                                            <a href={`mailto:${user.email}`} className="text-sm text-indigo-600 hover:text-indigo-900 flex items-center">
                                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                </svg>
                                                {user.email}
                                            </a>
                                            {user.phone && (
                                                <span className="text-sm text-gray-500 mt-0.5 flex items-center">
                                                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                    </svg>
                                                    {user.phone}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="py-2 px-6">
                                        <div className="text-sm text-gray-900 font-medium">{user.company || '-'}</div>
                                        <div className="text-sm text-gray-500 truncate max-w-xs" title={user.address}>
                                            {user.address || '-'}
                                        </div>
                                    </td>
                                    <td className="py-2 px-6 text-right whitespace-nowrap">
                                        <div className="flex justify-end space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={() => onEdit(user)}
                                                className="p-1 text-indigo-600 hover:bg-indigo-50 rounded"
                                                title="Edit User"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => onDelete(user)}
                                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                                                title="Delete User"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1 1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" className="py-8 text-center text-gray-500">
                                    No users found matching your search.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Footer Info */}
            <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                <p className="text-sm text-gray-600">
                    Showing {filteredUsers.length} of {users.length} total users
                </p>
            </div>
        </div>
    );
};

export default UserList;
